/**
 * Mouse context. Components wrapped in MouseProvider have
 * access to a smoothed mouse position that is defined relative to the screen
 * size using float values in the range -0.5->0.5, with the center of the screen
 * (the origin) at `{ x: 0, y: 0 }`.
 */

import type { SpringConfig } from '@react-spring/core'
import { Controller } from '@react-spring/core'
import type { FunctionComponent, MutableRefObject } from 'react'
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react'

export interface MouseProviderProps {
    config?: SpringConfig
}

type onUpdateRefType = () => void

interface MouseProviderValue {
    controller: Controller
    onUpdateRef?: MutableRefObject<onUpdateRefType>
}

const defaultValue = {
    controller: new Controller({ x: 0, y: 0 }),
}

// Provider
export const MouseProvider: FunctionComponent<MouseProviderProps> = ({
    config,
    ...rest
}) => {
    const onUpdateRef = useRef<onUpdateRefType>(
        null
    ) as MutableRefObject<onUpdateRefType>
    const mouseController = useMemo(
        () =>
            new Controller({
                x: 0,
                y: 0,
                config,
                onChange: () => onUpdateRef.current?.(),
            }),
        [config]
    )

    const handleMouseMove = useCallback(
        (event: any) => {
            const { innerWidth: width, innerHeight: height } = window
            const { clientX: mouseX, clientY: mouseY } = event

            // compute relative mouse positions [-0.5 -> 0.5]
            const mouse = { x: mouseX / width - 0.5, y: mouseY / height - 0.5 }

            // only update spring if mouse has moved since last update
            const { x, y } = mouseController.springs
            if (x.goal !== mouse.x || y.goal !== mouse.y) {
                mouseController.start(mouse)
            }
        },
        [mouseController]
    )

    // apply event listeners
    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove)
        return () => window.removeEventListener('mousemove', handleMouseMove)
    }, [handleMouseMove])

    return (
        <MouseContext.Provider
            value={{ controller: mouseController, onUpdateRef }}
            {...rest}
        />
    )
}

// Context
export const MouseContext = createContext<MouseProviderValue>(defaultValue)
